import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Printer, Download } from "lucide-react";
import { Logo } from "../components/Logo";
import { NDADocument, NDADisclosure } from "../components/NDADocument";

interface NDARecord {
  firstName:    string;
  lastName:     string;
  email:        string;
  phone:        string;
  isBusiness:   boolean;
  businessName: string | null;
  ndaVersion:   string;
  ndaPdfLink:   string | null;
  signature:    string | null;
  ipAddress:    string | null;
  createdAt:    string;
}

export function ClientNDAViewPage() {
  const navigate             = useNavigate();
  const { token }            = useParams<{ token: string }>();
  const [record, setRecord]  = useState<NDARecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setError("Invalid link."); setLoading(false); return; }
    fetch(`/api/requests/nda/${token}`)
      .then(async (res) => {
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "NDA record not found.");
        setRecord(data.data);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load NDA record."))
      .finally(() => setLoading(false));
  }, [token]);

  const acceptedAt = record
    ? new Date(record.createdAt).toLocaleString("en-ZA", {
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZoneName: "short",
      })
    : "";

  return (
    <div className="nda-page-wrapper min-h-screen bg-[#F9FAFB]">

      {/* Nav */}
      <nav className="print-hide bg-[#0D1520] border-b border-white/8 px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors text-xs"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <span className="text-white/20 text-xs">·</span>
          <Logo className="w-10 h-auto opacity-80" white />
        </div>
        {record && (
          <div className="flex items-center gap-2">
            {record.ndaPdfLink && (
              <a
                href={`/api/admin/nda/${record.ndaPdfLink}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white border border-white/15 hover:border-white/30 px-3 py-1.5 rounded transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
            )}
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white border border-white/15 hover:border-white/30 px-3 py-1.5 rounded transition-colors"
            >
              <Printer className="w-3.5 h-3.5" /> Print NDA
            </button>
          </div>
        )}
      </nav>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-10">

        {loading && (
          <div className="text-center py-24 text-[#4A5565] text-sm">Loading…</div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {record && !loading && (
          <>
            {/* Page header */}
            <div className="print-hide mb-6">
              <p className="text-xs uppercase tracking-widest text-[#7B1A1A] font-semibold mb-1">Your Legal Record</p>
              <h1 className="text-2xl font-semibold text-[#101828]">
                NDA Record — {record.firstName} {record.lastName}
              </h1>
              <p className="text-sm text-[#4A5565] mt-1">
                Accepted on {new Date(record.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" })}
                {" · "}{record.ndaVersion}
              </p>
            </div>

            {/* NDA document */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-sm mb-4">
              <div className="nda-scroll-container p-8 sm:p-10">
                <NDADocument
                  acceptedBy={`${record.firstName} ${record.lastName}`}
                  acceptedAt={acceptedAt}
                  email={record.email}
                  phone={record.phone}
                  isBusiness={record.isBusiness}
                  businessName={record.businessName ?? undefined}
                  ipAddress={record.ipAddress}
                  signature={record.signature ?? undefined}
                  adminView={true}
                />
              </div>
            </div>

            <NDADisclosure />
          </>
        )}
      </div>
    </div>
  );
}
